import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Github, Linkedin, Twitter, Link as LinkIcon, Mail, Phone, 
  MapPin, Download, Briefcase, GraduationCap, Code, Globe, 
  ExternalLink, Sparkles, Send, Award, FileText, Lock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { 
  resumeInfoApi, educationApi, experienceApi, projectsApi, 
  skillsApi, languagesApi, awardsApi 
} from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { onAuthStateChange } from '../lib/firebase';
import LiveCodingStats from '../components/portfolio/LiveCodingStats';
import { generatePortfolioPDF } from '../utils/portfolioPdfExport';


import GlassmorphismTheme from '../components/portfolio-templates/GlassmorphismTheme';
import TerminalTheme from '../components/portfolio-templates/TerminalTheme';
import Creative3DTheme from '../components/portfolio-templates/Creative3DTheme';
import NeonGlowTheme from '../components/portfolio-templates/NeonGlowTheme';
import RetroTheme from '../components/portfolio-templates/RetroTheme';
import NewspaperTheme from '../components/portfolio-templates/NewspaperTheme';

export default function PortfolioView() {
  const { resumeId } = useParams();
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get('preview') === 'true';

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  
  // Customization settings
  const [template, setTemplate] = useState('developer');
  const [color, setColor] = useState('blue');
  const [socials, setSocials] = useState({ github: '', leetcode: '', linkedin: '', twitter: '', custom: '' });
  const [isPublished, setIsPublished] = useState(true);
  
  // Contact form state
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMsg, setContactMsg] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    if (isPreviewMode) return;

    const unsubscribe = onAuthStateChange(() => {
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, [isPreviewMode]);

  // Helper: load profile data from localStorage (saved by PortfolioBuilder)
  const loadProfileFromLocalStorage = () => {
    const previewDataKey = `cvlogs_portfolio_preview_data_${resumeId}`;
    const savedPreviewData = localStorage.getItem(previewDataKey);
    if (savedPreviewData) {
      try {
        const parsed = JSON.parse(savedPreviewData);
        setProfileData(parsed);
        return true;
      } catch (e) {
        console.error('Error parsing saved profile data:', e);
      }
    }
    return false;
  };

  useEffect(() => {
    const handleUpdate = () => {
      if (isPreviewMode) {
        loadProfileFromLocalStorage();
        setLoading(false);
      }
    };

    if (isPreviewMode) {
      handleUpdate();
      window.addEventListener('portfolio-preview-updated', handleUpdate);
      window.addEventListener('storage', handleUpdate);
      return () => {
        window.removeEventListener('portfolio-preview-updated', handleUpdate);
        window.removeEventListener('storage', handleUpdate);
      };
    }

    if (resumeId) {
      // Immediately try loading from localStorage for instant display
      const hasLocalData = loadProfileFromLocalStorage();
      if (hasLocalData) {
        setLoading(false);
      }

      // Then try the API for fresh data (will override localStorage data if successful)
      if (authInitialized) {
        fetchFullProfile(!hasLocalData);
      }
    }
  }, [resumeId, isPreviewMode, authInitialized]);

  // Read preview params or load from local storage
  useEffect(() => {
    if (isPreviewMode) {
      setTemplate(searchParams.get('template') || 'developer');
      setColor(searchParams.get('color') || 'blue');
      setSocials({
        github: searchParams.get('github') || '',
        leetcode: searchParams.get('leetcode') || '',
        linkedin: searchParams.get('linkedin') || '',
        twitter: searchParams.get('twitter') || '',
        custom: searchParams.get('customLink') || ''
      });
      setIsPublished(true); // Always viewable in preview mode
    } else if (resumeId) {
      const saved = localStorage.getItem(`cvlogs_portfolio_${resumeId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setTemplate(parsed.template || 'developer');
          setColor(parsed.themeColor || 'blue');
          setSocials({
            github: parsed.githubUrl || '',
            leetcode: parsed.leetcodeUrl || '',
            linkedin: parsed.linkedinUrl || '',
            twitter: parsed.twitterUrl || '',
            custom: parsed.customLink || ''
          });
          setIsPublished(parsed.isPublished ?? true);
        } catch (e) {
          console.error(e);
        }
      } else {
        // Defaults - if no settings saved but data exists in localStorage, still show it
        setTemplate('developer');
        setColor('blue');
        setIsPublished(true);
      }
    }
  }, [resumeId, isPreviewMode, searchParams]);

  const trackAnalyticsEvent = (eventType, eventDetail = '') => {
    if (!resumeId) return;
    const key = `cvlogs_portfolio_analytics_${resumeId}`;
    const raw = localStorage.getItem(key);
    let stats = {
      totalVisits: 0,
      uniqueVisitors: 0,
      socialClicks: 0,
      cvDownloads: 0,
      recentClicks: [],
      dailyVisits: { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 }
    };
    
    if (raw) {
      try {
        stats = { ...stats, ...JSON.parse(raw) };
      } catch (e) {
        console.error(e);
      }
    }

    const today = new Date();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const currentDay = dayNames[today.getDay()];

    if (eventType === 'visit') {
      stats.totalVisits += 1;
      stats.dailyVisits[currentDay] = (stats.dailyVisits[currentDay] || 0) + 1;
      
      const sessionKey = `cvlogs_portfolio_visited_${resumeId}`;
      if (!sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, 'true');
        stats.uniqueVisitors += 1;
      }
    } else if (eventType === 'click') {
      stats.socialClicks += 1;
      stats.recentClicks.unshift({
        type: eventDetail,
        time: new Date().toISOString(),
        location: isPreviewMode ? 'Local Preview' : 'Web View'
      });
      stats.recentClicks = stats.recentClicks.slice(0, 5);
    } else if (eventType === 'download') {
      stats.cvDownloads += 1;
    }

    localStorage.setItem(key, JSON.stringify(stats));
    
    // Dispatch custom events for live syncing
    window.dispatchEvent(new Event('portfolio-analytics-updated'));
    try {
      window.parent.dispatchEvent(new Event('portfolio-analytics-updated'));
    } catch (e) {}
  };

  useEffect(() => {
    if (profileData && resumeId) {
      trackAnalyticsEvent('visit');
    }
  }, [profileData, resumeId]);

  const fetchFullProfile = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      // Fetch core info, education, experience, skills, projects, and awards in parallel
      const [core, edu, exp, proj, sk, lang, aw] = await Promise.all([
        resumeInfoApi.get(resumeId).catch(() => null),
        educationApi.list(resumeId).catch(() => []),
        experienceApi.list(resumeId).catch(() => []),
        projectsApi.list(resumeId).catch(() => []),
        skillsApi.list(resumeId).catch(() => []),
        languagesApi.list(resumeId).catch(() => []),
        awardsApi.list(resumeId).catch(() => [])
      ]);

      if (core) {
        const freshData = {
          core,
          education: Array.isArray(edu) ? edu : [],
          experience: Array.isArray(exp) ? exp : [],
          projects: Array.isArray(proj) ? proj : [],
          skills: Array.isArray(sk) ? sk : [],
          languages: Array.isArray(lang) ? lang : [],
          awards: Array.isArray(aw) ? aw : []
        };
        setProfileData(freshData);
        // Also update localStorage so future visits load instantly
        localStorage.setItem(`cvlogs_portfolio_preview_data_${resumeId}`, JSON.stringify(freshData));
      }
    } catch (e) {
      console.error('Error fetching full profile details:', e);
      // If API fails, try localStorage as final fallback
      loadProfileFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMsg.trim()) return;
    setFormSubmitted(true);
    setTimeout(() => {
      setContactName('');
      setContactEmail('');
      setContactMsg('');
      setFormSubmitted(false);
      alert('Message sent successfully!');
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <LoadingSpinner size="lg" logoSpinner={true} text="Loading Portfolio..." />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
        <Lock className="w-16 h-16 text-slate-500 mb-4 animate-pulse" />
        <h2 className="text-xl font-bold mb-2">Portfolio Not Found</h2>
        <p className="text-slate-400 text-sm max-w-sm text-center">
          The requested portfolio might be configured incorrectly or does not exist on our servers.
        </p>
      </div>
    );
  }

  // Check visibility status
  if (!isPublished && !isPreviewMode) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white p-6 text-center">
        <div className="p-4 bg-slate-900 rounded-3xl mb-4 border border-slate-800">
          <Lock className="w-12 h-12 text-blue-500" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Portfolio is Private</h2>
        <p className="text-slate-400 text-sm max-w-md">
          This personal portfolio website is currently in draft mode. The owner has not published it yet.
        </p>
      </div>
    );
  }

  const { core, education, experience, projects, skills, languages, awards } = profileData;

  // Accent Colors Mapper
  const colorMap = {
    blue: { primary: 'text-blue-500', bg: 'bg-blue-600', border: 'border-blue-500/20', hover: 'hover:bg-blue-700', ring: 'focus:ring-blue-500/20', gradient: 'from-blue-500 to-indigo-600' },
    emerald: { primary: 'text-emerald-500', bg: 'bg-emerald-600', border: 'border-emerald-500/20', hover: 'hover:bg-emerald-700', ring: 'focus:ring-emerald-500/20', gradient: 'from-emerald-500 to-teal-600' },
    purple: { primary: 'text-purple-500', bg: 'bg-purple-600', border: 'border-purple-500/20', hover: 'hover:bg-purple-700', ring: 'focus:ring-purple-500/20', gradient: 'from-purple-500 to-pink-600' },
    rose: { primary: 'text-rose-500', bg: 'bg-rose-600', border: 'border-rose-500/20', hover: 'hover:bg-rose-700', ring: 'focus:ring-rose-500/20', gradient: 'from-rose-500 to-red-600' },
    slate: { primary: 'text-slate-500', bg: 'bg-slate-700', border: 'border-slate-500/20', hover: 'hover:bg-slate-800', ring: 'focus:ring-slate-500/20', gradient: 'from-slate-600 to-slate-800' }
  };

  const activeColor = colorMap[color] || colorMap.blue;

  // Framer Motion Animation Settings
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, cubicBezier: [0.16, 1, 0.3, 1] }
  };

  // --- TEMPLATE RENDERING ---

  if (template === 'glassmorphism') return <GlassmorphismTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'terminal') return <TerminalTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'creative3d') return <Creative3DTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'neonglow') return <NeonGlowTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'retro') return <RetroTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'newspaper') return <NewspaperTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;

  // 1. DEVELOPER (DARK THEME)
  if (template === 'developer') {
    return (
      <div className="min-h-screen bg-[#0b0f19] text-slate-100 font-sans selection:bg-blue-500 selection:text-white pb-16">
        <Helmet><title>{core.name} | Portfolio</title></Helmet>

        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex items-center justify-center px-4 overflow-hidden border-b border-slate-900">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 border ${activeColor.border} text-slate-300`}
            >
              <Sparkles className={`w-3.5 h-3.5 ${activeColor.primary}`} />
              <span>Available for Opportunities</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="text-4xl sm:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-white via-slate-100 to-slate-400 bg-clip-text text-transparent"
            >
              {core.name}
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-slate-400 max-w-xl mx-auto text-sm sm:text-base leading-relaxed"
            >
              {core.summary || 'A passionate professional dedicated to building high-quality projects and solving complex technical challenges.'}
            </motion.p>

            {/* Social Links */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-3.5 pt-4"
            >
              {socials.github && (
                <a href={socials.github} target="_blank" rel="noreferrer" className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full transition-all text-slate-300 hover:text-white">
                  <Github className="w-5 h-5" />
                </a>
              )}
              {socials.linkedin && (
                <a href={socials.linkedin} target="_blank" rel="noreferrer" className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full transition-all text-slate-300 hover:text-white">
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {socials.twitter && (
                <a href={socials.twitter} target="_blank" rel="noreferrer" className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full transition-all text-slate-300 hover:text-white">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {socials.custom && (
                <a href={socials.custom} target="_blank" rel="noreferrer" className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full transition-all text-slate-300 hover:text-white">
                  <LinkIcon className="w-5 h-5" />
                </a>
              )}
            </motion.div>

            {/* Download CV Button */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <button
                onClick={() => { generatePortfolioPDF(profileData); trackAnalyticsEvent('download'); }}
                className={`inline-flex items-center gap-2 px-6 py-2.5 ${activeColor.bg} ${activeColor.hover} text-white rounded-full text-sm font-semibold transition-all shadow-lg hover:shadow-xl`}
              >
                <Download className="w-4 h-4" />
                Download CV
              </button>
            </motion.div>
          </div>
        </section>

        {/* Experience Timeline */}
        {experience.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 py-20 space-y-12">
            <motion.div {...fadeInUp} className="text-center space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold">Work History</h2>
              <div className={`w-12 h-1 ${activeColor.bg} mx-auto rounded-full`} />
            </motion.div>

            <div className="relative border-l border-slate-800 ml-4 space-y-8">
              {experience.map((exp, idx) => (
                <motion.div 
                  key={idx} 
                  {...fadeInUp}
                  className="relative pl-8 group"
                >
                  <div className="absolute -left-1.5 top-1.5 w-3 h-3 rounded-full bg-slate-900 border-2 border-slate-700 group-hover:border-blue-500 transition-colors" />
                  <div className="bg-slate-900/40 border border-slate-800/80 rounded-2xl p-5 hover:border-slate-800 transition-all">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                      {exp.start_date} - {exp.end_date || 'Present'}
                    </span>
                    <h3 className="text-base font-bold text-white">{exp.job_title}</h3>
                    <div className="text-xs text-slate-400 font-semibold mb-2">{exp.company}</div>
                    <p className="text-xs text-slate-400 leading-relaxed">{exp.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Projects Grid */}
        {projects.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 py-20 space-y-12">
            <motion.div {...fadeInUp} className="text-center space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold">Featured Work</h2>
              <div className={`w-12 h-1 ${activeColor.bg} mx-auto rounded-full`} />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.map((proj, idx) => (
                <motion.div 
                  key={idx} 
                  {...fadeInUp}
                  className="bg-slate-900/40 border border-slate-800/60 rounded-2xl p-6 hover:border-slate-800 hover:bg-slate-900/60 transition-all flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-950/60 w-fit rounded-xl border border-slate-800">
                      <Code className={`w-5 h-5 ${activeColor.primary}`} />
                    </div>
                    <h3 className="text-lg font-bold text-white">{proj.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{proj.description}</p>
                  </div>
                  {proj.link && (
                    <a href={proj.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-400 mt-4">
                      <span>View Project</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Skills & Categories */}
        {skills.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 py-20 space-y-12">
            <motion.div {...fadeInUp} className="text-center space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold">Skills & expertise</h2>
              <div className={`w-12 h-1 ${activeColor.bg} mx-auto rounded-full`} />
            </motion.div>

            <motion.div 
              {...fadeInUp}
              className="flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto"
            >
              {skills.map((sk, idx) => (
                <span key={idx} className="px-3.5 py-1.5 bg-slate-900/80 border border-slate-800 rounded-full text-xs font-medium text-slate-300">
                  {sk.name || sk}
                </span>
              ))}
            </motion.div>
          </section>
        )}

        {/* Live Coding Stats */}
        {(socials.github || socials.leetcode) && (
          <section className="max-w-4xl mx-auto px-4 py-10">
             <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="dark" />
          </section>
        )}

        {/* Contact Form */}
        <section className="max-w-xl mx-auto px-4 py-20 space-y-8">
          <motion.div {...fadeInUp} className="text-center space-y-2">
            <h2 className="text-2xl font-bold">Get In Touch</h2>
            <p className="text-xs text-slate-400">Feel free to drop a message or reach out for work!</p>
          </motion.div>

          <motion.form 
            {...fadeInUp}
            onSubmit={handleContactSubmit}
            className="space-y-4"
          >
            <input
              type="text"
              required
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Your Name"
              className="w-full px-4 py-3 bg-slate-900/60 border border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-slate-700"
            />
            <input
              type="email"
              required
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="Email Address"
              className="w-full px-4 py-3 bg-slate-900/60 border border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-slate-700"
            />
            <textarea
              required
              rows={4}
              value={contactMsg}
              onChange={(e) => setContactMsg(e.target.value)}
              placeholder="Write your message..."
              className="w-full p-4 bg-slate-900/60 border border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-slate-700 resize-none"
            />
            <button
              type="submit"
              className={`w-full py-3 bg-blue-600 hover:bg-blue-700 font-bold text-white rounded-xl text-xs transition-all shadow-md flex items-center justify-center gap-1.5`}
            >
              <Send className="w-3.5 h-3.5" />
              <span>Send Message</span>
            </button>
          </motion.form>
        </section>

      </div>
    );
  }

  // 2. MINIMALIST (LIGHT SERIF THEME)
  if (template === 'minimalist') {
    return (
      <div className="min-h-screen bg-[#faf9f6] text-slate-800 font-serif selection:bg-slate-800 selection:text-white pb-20">
        <Helmet><title>{core.name}</title></Helmet>

        {/* Header Name */}
        <header className="max-w-3xl mx-auto px-6 pt-16 pb-8 border-b border-slate-200 text-center sm:text-left flex flex-col sm:flex-row justify-between items-center sm:items-end gap-6">
          <div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-slate-900">{core.name}</h1>
            <p className="text-slate-500 italic mt-2 text-sm font-sans">{core.summary || 'Professional Portfolio'}</p>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <button
              onClick={() => { generatePortfolioPDF(profileData); trackAnalyticsEvent('download'); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 hover:border-slate-800 hover:bg-slate-800 hover:text-white rounded text-xs font-sans font-semibold transition-all"
            >
              <Download className="w-3.5 h-3.5" />
              Download CV
            </button>
          </motion.div>
        </header>

        {/* Main Sections */}
        <main className="max-w-3xl mx-auto px-6 py-12 space-y-12">
          {/* About Summary */}
          {core.summary && (
            <motion.section {...fadeInUp} className="space-y-4">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Profile</h2>
              <p className="text-base leading-relaxed text-slate-700 font-light text-justify">
                {core.summary}
              </p>
            </motion.section>
          )}

          {/* Experience */}
          {experience.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Experience</h2>
              <div className="space-y-6">
                {experience.map((exp, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-l border-slate-200 pl-4">
                    <span className="text-xs text-slate-400 font-sans font-semibold col-span-1">
                      {exp.start_date} - {exp.end_date || 'Present'}
                    </span>
                    <div className="col-span-3 space-y-1">
                      <h3 className="text-base font-semibold text-slate-900">{exp.job_title}</h3>
                      <div className="text-xs text-slate-500 font-sans font-medium">{exp.company}</div>
                      <p className="text-xs text-slate-600 leading-relaxed text-justify mt-1">{exp.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Featured projects */}
          {projects.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Projects</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {projects.map((proj, idx) => (
                  <div key={idx} className="space-y-2 p-4 border border-slate-200/80 rounded-xl bg-white/40">
                    <h3 className="text-base font-bold text-slate-900">{proj.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{proj.description}</p>
                    {proj.link && (
                      <a href={proj.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-sans text-slate-600 hover:text-slate-900 font-semibold underline">
                        <span>Project Source</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Skills list */}
          {skills.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-4">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Expertise</h2>
              <div className="flex flex-wrap gap-2">
                {skills.map((sk, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-xs font-sans text-slate-600">
                    {sk.name || sk}
                  </span>
                ))}
              </div>
            </motion.section>
          )}

          {/* Live Coding Stats */}
          {(socials.github || socials.leetcode) && (
            <motion.section {...fadeInUp} className="space-y-4 pt-6">
               <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="light" />
            </motion.section>
          )}

          {/* Footer Contact Details */}
          <motion.section {...fadeInUp} className="border-t border-slate-200 pt-12 text-center space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Contact Details</h2>
            <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-600 font-sans">
              {core.email && (
                <span className="flex items-center gap-1 justify-center">
                  <Mail className="w-3.5 h-3.5" />
                  {core.email}
                </span>
              )}
              {core.phone && (
                <span className="flex items-center gap-1 justify-center">
                  <Phone className="w-3.5 h-3.5" />
                  {core.phone}
                </span>
              )}
              {core.city && (
                <span className="flex items-center gap-1 justify-center">
                  <MapPin className="w-3.5 h-3.5" />
                  {core.city}, {core.country}
                </span>
              )}
            </div>

            {/* Social Icons */}
            <div className="flex justify-center gap-3 pt-2">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800"><Github className="w-5 h-5" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800"><Linkedin className="w-5 h-5" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800"><Twitter className="w-5 h-5" /></a>}
            </div>
          </motion.section>
        </main>
      </div>
    );
  }

  // 3. CORPORATE (PROFESSIONAL MULTI-COLOR THEME)
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-blue-600 selection:text-white pb-16">
      <Helmet><title>{core.name} | Executive Profile</title></Helmet>

      {/* Modern Top Navigation Bar */}
      <nav className="bg-slate-900 text-white sticky top-0 z-30 shadow-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="font-extrabold tracking-wider text-sm sm:text-base uppercase">{core.name}</span>
          <a
            href={`mailto:${core.email}`}
            className={`px-4 py-2 ${activeColor.bg} ${activeColor.hover} text-white font-bold rounded-lg text-xs transition-colors flex items-center gap-1.5 shadow-sm`}
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Hire Me</span>
          </a>
        </div>
      </nav>

      {/* Intro Header */}
      <section className="bg-slate-900 text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600/10 rounded-full blur-2xl" />
        <div className="max-w-4xl mx-auto space-y-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-widest"
          >
            <Briefcase className="w-4 h-4" />
            <span>Executive Portfolio</span>
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-5xl font-black tracking-tight"
          >
            {core.name}
          </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl"
            >
              {core.summary}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="pt-4"
            >
              <button
                onClick={() => { generatePortfolioPDF(profileData); trackAnalyticsEvent('download'); }}
                className={`inline-flex items-center gap-2 px-5 py-2 border-2 ${activeColor.border} hover:bg-slate-800 rounded-lg text-sm font-bold text-white transition-all`}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </motion.div>
        </div>
      </section>

      {/* Main Body */}
      <main className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Left Column (Main Experience & Projects) */}
        <div className="lg:col-span-8 space-y-12">
          {/* Work History */}
          {experience.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6 bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm">
              <h2 className="text-lg font-bold border-b border-slate-100 pb-3 flex items-center gap-2">
                <Briefcase className="text-blue-500 w-5 h-5" />
                <span>Professional Experience</span>
              </h2>

              <div className="space-y-6">
                {experience.map((exp, idx) => (
                  <div key={idx} className="relative pl-6 border-l-2 border-slate-200/80 last:border-0 pb-4">
                    <div className="absolute -left-1.5 top-1.5 w-3 h-3 rounded-full bg-blue-500 border border-white" />
                    <span className="text-[10px] font-bold text-slate-400 block mb-1">
                      {exp.start_date} - {exp.end_date || 'Present'}
                    </span>
                    <h3 className="text-base font-bold text-slate-800">{exp.job_title}</h3>
                    <div className="text-xs text-slate-500 font-semibold mb-2">{exp.company}</div>
                    <p className="text-xs text-slate-500 leading-relaxed">{exp.description}</p>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Project Grid */}
          {projects.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Code className="text-blue-500 w-5 h-5" />
                <span>Featured Projects</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {projects.map((proj, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                    <div className="space-y-2">
                      <h3 className="font-bold text-slate-800 text-sm">{proj.title}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{proj.description}</p>
                    </div>
                    {proj.link && (
                      <a href={proj.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 mt-4">
                        <span>Check details</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Live Coding Stats */}
          {(socials.github || socials.leetcode) && (
             <motion.section {...fadeInUp} className="pt-6">
                <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="light" />
             </motion.section>
          )}
        </div>

        {/* Right Column (Skills, Details, Socials) */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* Contact details */}
          <motion.div {...fadeInUp} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-bold text-slate-800 text-sm border-b border-slate-100 pb-2.5">Information</h3>
            <div className="space-y-3 text-xs text-slate-600">
              {core.email && (
                <div className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="truncate">{core.email}</span>
                </div>
              )}
              {core.phone && (
                <div className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{core.phone}</span>
                </div>
              )}
              {core.city && (
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{core.city}, {core.country}</span>
                </div>
              )}
            </div>
            
            {/* Social links row */}
            <div className="flex items-center gap-2 pt-2">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-800 hover:border-slate-300"><Github className="w-4 h-4" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-800 hover:border-slate-300"><Linkedin className="w-4 h-4" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-800 hover:border-slate-300"><Twitter className="w-4 h-4" /></a>}
            </div>
          </motion.div>

          {/* Skills */}
          {skills.length > 0 && (
            <motion.div {...fadeInUp} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-800 text-sm border-b border-slate-100 pb-2.5">Competencies</h3>
              <div className="flex flex-wrap gap-1.5">
                {skills.map((sk, idx) => (
                  <span key={idx} className="px-2.5 py-1 bg-slate-100/80 rounded-lg text-xs font-semibold text-slate-600">
                    {sk.name || sk}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Education list */}
          {education.length > 0 && (
            <motion.div {...fadeInUp} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-800 text-sm border-b border-slate-100 pb-2.5">Academic Record</h3>
              <div className="space-y-4">
                {education.map((edu, idx) => (
                  <div key={idx} className="space-y-1 text-xs">
                    <div className="font-bold text-slate-800">{edu.degree}</div>
                    <div className="text-slate-500 font-semibold">{edu.school}</div>
                    <div className="text-slate-400 font-sans">{edu.start_date} - {edu.end_date || 'Present'}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

        </div>

      </main>

    </div>
  );
}
